
import { Injectable } from '@angular/core';
import { ApiService } from '../api.service';
import { SharedDataService } from './shared-data.service';
import { UploadCollection } from '../objects/uploadCollection';
import { UPLOAD_ITEM_STATUS } from '../objects/uploadStatuses';
import { UploadItem } from '../objects/uploadItem';
import { HttpEventType, HttpResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import * as he from 'he';

const MAX_CHUNK_SIZE = 104857600; // 100MB
const DEFAULT_CHUNK_SIZE = 10485760; // 10MB
const RESUME_INTERVAL = 10000;


interface ProgressUpload {
  totalBytes: number,
  receivedBytes: number
}

interface QuotaResponse {
  account_used: number;
  account_quota: number;
  user_quota: number;
  user_used: number;
}



@Injectable({
  providedIn: 'root'
})


export class UploadService {

  public uploadCollection = new UploadCollection(this);
  public isUploadPaused = false;
  private activeUploadPath = '/'
  private resumeInterval = null;
  public listingCurrentPath = '/';
  private startButtonDisabledSource = new Subject<boolean>();
  private startButtonStatusSource = new Subject<string>();
  public startButtonDisabledObservable = this.startButtonDisabledSource.asObservable();
  public startButtonStatusObservable = this.startButtonStatusSource.asObservable();

  constructor(
    private apiService: ApiService,
    private sharedDataService: SharedDataService
  ) { }

  public setCurrentPath(path) {
    if (!this.uploadCollection.hasActiveUpload) {
      this.activeUploadPath = path;
    }
    this.listingCurrentPath = path;
  }

  private isValidName(value: string, isFolder: boolean, shouldSkipUnique: boolean = false) {
    const str = value.replace(/ /g, '');
    if (str[0] !== '.' && !shouldSkipUnique) {
      if (isFolder) {
        if (this.uploadCollection.dirs.some(dir => dir.name === value)) {
          this.sharedDataService.pushNotification({ text: `Folder ${value} is already selected for upload`, type: 'error' });
          return false
        }
      } else {
        if (this.uploadCollection.standAloneFiles.some(file => file.name === value)) {
          this.sharedDataService.pushNotification({ text: `File ${value} is already selected for upload`, type: 'error' });
          return false
        }
      }
    }
    if (!str) {
      this.sharedDataService.pushNotification({ text: 'File name is required.', type: 'error' });
      return false
    }
    if (value.length > 220) {
      this.sharedDataService.pushNotification({ text: 'Maximum allowed name length is 220 characters.', type: 'error' });
      return false
    }

    if (str.match(/\//)) {
      this.sharedDataService.pushNotification({ text: 'Slashes are forbidden in file name.', type: 'error' });
      return false
    }
    if (str === '.' || str === '..') {
      this.sharedDataService.pushNotification({ text: 'Dots are forbidden in file name.', type: 'error' })
      return false
    }
    return true
  }

  public handleFilesFromInput(files: File[]) {
    const filesAsArray = Array.from(files);
    if (filesAsArray.every(file => !!file['webkitRelativePath'])) {
      const path = files[0]['webkitRelativePath'].split('/')
      const isNewFolder = this.handleAddNewFolder({ name: path[0], fullPath: `/${path[0]}` })
      if (!isNewFolder) {
        return
      }
    }
    Array.from(files).forEach(file => {
      this.handleAddNewFile({ file, fullPath: `/${file['webkitRelativePath'] || file.name}` })
    })
  }

  public handleAddNewFile({ file, fullPath }) {
    const splitedPath = fullPath.split('/')
    if (splitedPath.length > 2) {
      // if length greater that 2 fullPath is like /dir/file.txt, it means that file is uploaded like folder
      if (!this.isValidName(file.name, false, true)) {
        return
      }
      this.uploadCollection.addFileToFolder({ originalFile: file, fullPath }, splitedPath[1])
    } else {
      // if length equal 2 it mean that full path is like /file1.txt, and it is just a file
      if (!this.isValidName(file.name, false, false)) {
        return
      }
      this.uploadCollection.addStandAloneFile({ originalFile: file, fullPath })

    }
    this.updateStartButton();
  }

  public handleAddNewFolder({ name, fullPath }) {
    if (!this.isValidName(name, true)) {
      return false
    }
    this.uploadCollection.mkdir({ name, fullPath })
    this.updateStartButton();
    return true
  }

  public handleStartButton() {
    if (this.isUploadPaused) {
      this.resumeUpload()
    } else {
      const totalBytes = this.uploadCollection.files.reduce((a, b) => a + b.size, 0);
      this.getQuota(totalBytes)
    }
  }

  // Quota verify
  public getQuota(payloadSize) {
    this.apiService.checkQuota()
      .subscribe(
        (res: QuotaResponse) => this.handleQuotaResponse(res, payloadSize))
  }

  private handleQuotaResponse({
    account_quota: accountQuota,
    account_used: accountUsed,
    user_quota: userQuota,
    user_used: userUsed
  }, payloadSize: number) {
    if (userQuota && (userUsed + payloadSize > userQuota) || accountQuota && (accountUsed + payloadSize > accountQuota)) {
      this.sharedDataService.pushNotification({ text: 'Sorry, there is not enough free space to upload selected files.', type: 'info' })
      this.startButtonDisabledSource.next(false);
      return
    }
    this.prepareUpload()
    this.updateStartButton();
  }
  //

  // If chunk upload failed, we need to send it again
  private revertFileReadPosition(file) {
    const buff = file.end;
    file.end = file.start;
    file.start = buff - file.chunkSize;
  };

  public prepareUpload() {
    this.uploadCollection.hasActiveUpload = true;
    this.uploadCollection.files
      .filter(file => ![UPLOAD_ITEM_STATUS.UPLOAD_CANCELED,
      UPLOAD_ITEM_STATUS.UPLOAD_ERROR,
      UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR].includes(file.state))
      .forEach(file => {
        if (file.state) {
          this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_STARTED)
        }
      })
    this.updateStartButton();
    this.startUpload()
  }

  private handleAddFileToListing(file) {
    if (file.parent) {
      const prefixArrayOfUploaded = file.uploadedName.split('/');
      const prefixArrayOfPath = file.fullPath.split('/')
      const fullPath = prefixArrayOfPath.slice(0, [prefixArrayOfPath.length - 1]).join('/');
      const uploadedPath = prefixArrayOfUploaded.slice(0, [prefixArrayOfUploaded.length - 1]).join('/');
      const pathForCompate = uploadedPath.replace(`${fullPath}`, '') || '/';
      if (this.listingCurrentPath === pathForCompate) {
        this.sharedDataService.addFileToList(file)
      }
    } else {
      const prefixArray = file.uploadedName.split('/');
      const uploadedPath = prefixArray.slice(0, [prefixArray.length - 1]).join('/') || '/';
      if (uploadedPath === this.listingCurrentPath) {
        this.sharedDataService.addFileToList(file)
      }
    }
  }

  public startUpload(file?: UploadItem) {
    file = file || this.uploadCollection.getNextFile();
    if (file) {
      file.uploadURL = this.activeUploadPath !== '/' ? this.activeUploadPath + file.fullPath : file.fullPath;
      this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_UPLOADING);
      this.updateStartButton();
      this.readFileAndUpload(file, 0)
    } else {
      this.finishUpload();
    }
  }

  private readFileAndUpload(file, uploadedBytes) {
    const fileContent = file._originalFile;
    if (uploadedBytes) {
      file.totalUploaded += uploadedBytes;
    }
    if (file.totalUploaded < file.size || (file.size === 0 && uploadedBytes === file.size)) {

      file.chunkSize = Math.min(Math.min(file.size - file.totalUploaded, file.chunkSize || DEFAULT_CHUNK_SIZE), MAX_CHUNK_SIZE);

      const blob = fileContent.slice(file.totalUploaded, file.totalUploaded + file.chunkSize);

      file.start = file.end;
      file.end = file.start + file.chunkSize;

      let lastUploadedBytes = 0;
      let eachUploadedBytes = 0;
      let startTime = Date.now();

      // For abort request
      file.cancelSource = new Subject<void>();
      file.cancelObservable = file.cancelSource.asObservable();
      file.xhr = this.apiService.uploadChunks(file.uploadURL, blob, file.start, file.end - 1, file.size, file.cancelObservable)
        .subscribe(event => {
          // Check if event type is progress, to keep it fresh
          if (event.type === HttpEventType.UploadProgress) {
            const eachProgressTime = Date.now();
            eachUploadedBytes = event.loaded - lastUploadedBytes;
            lastUploadedBytes = event.loaded;
            file.chunkUploadSpeed = eachUploadedBytes / ((eachProgressTime - startTime) / 1000);
            this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_UPLOADING)
            this.updateStartButton();
            this.onConcurrentProgress(file, file.start + event.loaded + 1);
            startTime = eachProgressTime;

            return
          }
          if (event instanceof HttpResponse) {
            if (event.status === 200) {
              file.uploadedName = he.decode(event.body['file_name']);
              this.onConcurrentProgress(file, file.end + 1);
              file.chunkSize = Math.min(file.chunkSize * 2, MAX_CHUNK_SIZE)
              if (file.size === 0) {
                this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_FINISHED)
                this.updateStartButton();
                this.startUpload();
                return
              }
              this.readFileAndUpload(file, file.end - file.start);
              this.handleAddFileToListing(file)
            }
          }
        }, err => {
          if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_CANCELED) {
            return
          }
          this.isUploadPaused = true

          if (err) {
            const { exception: { status, error } } = err;

            if (!status) { // no connection or abort
              this.sharedDataService.pushNotification({
                text: 'Sorry, Internet connection error occurred. Please check your Internet connection.',
                type: 'error'
              })
              file.errorMsg = 'Please check your connectivity';
              this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR);
              this.setResumeWorker();
              this.updateStartButton();
              this.onConcurrentProgress(file, file.from + 1); // revert progress
              this.revertFileReadPosition(file);
              file.chunkSize = DEFAULT_CHUNK_SIZE;
              return
            }

            this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_ERROR)
            this.updateStartButton();

            if (status === 400) {
              this.sharedDataService.pushNotification({ text: error.message, type: 'error' })
              file.errorMsg = error.message;
              this.startUpload();
            }
            if (status === 403) {
              this.sharedDataService.pushNotification({ text: 'Sorry, you have exceeded storage quota', type: 'error' })
              this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR);
              this.updateStartButton();
              file.errorMsg = 'Sorry, you have exceeded storage quota';
            }
            if (status === 404) {
              this.sharedDataService.pushNotification({ text: 'Folder you uploading to does not exist already', type: 'error' })
              file.errorMsg = 'Folder you uploading to does not exist already ';
            }
            if (status === 413) {
              this.sharedDataService.pushNotification({ text: 'File is too big', type: 'error' })
              file.errorMsg = 'File is too big';
              this.startUpload()
            }
            if (status >= 500) {
              this.sharedDataService.pushNotification({ text: 'Something went wrong, please try again later', type: 'error' })
              file.errorMsg = 'Something went wrong, please try again later';
            }
          }

          this.onConcurrentProgress(file, file.start + 1); // revert progress
          this.revertFileReadPosition(file);
        })

    } else if (file.totalUploaded === file.size) {

      this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_FINISHED)
      this.updateStartButton();

      // Upload next started file
      this.startUpload();
    }
  }

  private setResumeWorker() {
    this.resumeInterval = setTimeout(() => {
      if (this.isUploadPaused) {
        this.resumeUpload();
      }
    }, RESUME_INTERVAL);
  }

  public resumeUpload() {
    this.clearResumeInterval();
    this.uploadCollection.files.forEach(file => {
      if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR) {
        this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_STARTED)
      }
    })
    this.updateStartButton();
    this.isUploadPaused = false;
    this.startUpload();
  };


  private onConcurrentProgress(file: UploadItem, received: number) {
    file.received = received;
    const { totalBytes, receivedBytes } = this.uploadCollection.files.reduce((a: ProgressUpload, b: UploadItem) => {
      if ([UPLOAD_ITEM_STATUS.UPLOAD_ERROR,
      UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR,
      UPLOAD_ITEM_STATUS.UPLOAD_CANCELED].includes(b.state)) {
        // Ignore size of files on which happened error or was canceled
        return a
      }
      a.totalBytes += b.size;
      a.receivedBytes += b.received
      return a
    }, { totalBytes: 0, receivedBytes: 0 });
    if (totalBytes) {
      const progress = Math.round((100 * receivedBytes) / totalBytes);
      if (progress < 100) {
        this.sharedDataService.updateProgress((Math.round((100 * receivedBytes) / totalBytes)));
      }
    }
  }

  public handleRemoveItem(file: UploadItem, newState: string) {
    if (newState === UPLOAD_ITEM_STATUS.UPLOAD_FINISHED) {
      this.uploadCollection.remove(file);
      if (!this.uploadCollection.hasActiveUpload) {
        this.sharedDataService.updateProgress(100);
      }
    }
    if (newState === UPLOAD_ITEM_STATUS.UPLOAD_CANCELED) {
      this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_CANCELED)
      this.updateStartButton();
      if (!this.uploadCollection.hasActiveUpload) {
        this.sharedDataService.updateProgress(-1);
      }
    }
  }

  public handleDeleteAll() {
    this.uploadCollection.files.forEach(file => {
      this.uploadCollection.changeFileState(file, UPLOAD_ITEM_STATUS.UPLOAD_CANCELED)
    })
    this.updateStartButton();
    this.sharedDataService.updateProgress(-1);
    this.uploadCollection.clear();
    this.isUploadPaused = false;
    this.uploadCollection.hasActiveUpload = false;

  };

  private clearResumeInterval() {
    if (this.resumeInterval) {
      clearTimeout(this.resumeInterval);
      this.resumeInterval = null;
    }
  }

  private finishUpload() {

    this.isUploadPaused = false;
    this.uploadCollection.hasActiveUpload = false;
    this.clearResumeInterval();
    this.uploadCollection.items
      .filter(item => item.state === UPLOAD_ITEM_STATUS.UPLOAD_FINISHED)
      .forEach(item => {
        this.handleRemoveItem(item, UPLOAD_ITEM_STATUS.UPLOAD_FINISHED);
      });
    if (this.uploadCollection.items.some(file =>
      [UPLOAD_ITEM_STATUS.UPLOAD_CANCELED, UPLOAD_ITEM_STATUS.UPLOAD_ERROR].includes(file.state))) {
      this.sharedDataService.updateProgress(-1);
      this.sharedDataService.closeDialog(false); // refresh file listing without close popup
    } else {
      this.uploadCollection.clear();
      this.sharedDataService.closeDialog(true); // refresh file listing with close popup
    }
  };

  private updateStartButton() {
    const { countQueuedUploads, countPausedUploads } =
      this.uploadCollection.files.reduce((a, file) => {
        if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_QUEUED) {
          a.countQueuedUploads++;

        }
        if (file.state === UPLOAD_ITEM_STATUS.UPLOAD_PAUSED_ON_ERROR) {
          a.countQueuedUploads++;
          a.countPausedUploads++;
        }
        return a
      }, { countQueuedUploads: 0, countPausedUploads: 0 });

    this.startButtonDisabledSource.next(!(countQueuedUploads || countPausedUploads));

    if (countPausedUploads) {
      // Network error, quota error
      this.startButtonStatusSource.next(UPLOAD_ITEM_STATUS.RESUME_UPLOAD);
    } else {
      this.startButtonStatusSource.next(UPLOAD_ITEM_STATUS.START_UPLOAD);
    }

  }

}
